import React from 'react';
import { Box, ChevronDownIcon, ChevronUpIcon, Text } from '@razorpay/blade/components';
import { FIELD_TYPE_META, FIELD_TYPE_OPTIONS } from '../../../constants';
import { EmployeeFieldValueType } from '../../../types';
import {
  FieldTypeButton,
  FieldTypeMenu,
  FieldTypeOption,
  FieldTypeOptionHelper,
} from '../../../styles';

type FieldTypeSelectorProps = {
  valueType: EmployeeFieldValueType | '';
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (valueType: EmployeeFieldValueType) => void;
};

export const FieldTypeSelector = ({
  valueType,
  isOpen,
  onToggle,
  onSelect,
}: FieldTypeSelectorProps) => {
  const selectedOption = FIELD_TYPE_OPTIONS.find((option) => option.value === valueType);
  const selectedTypeMeta = valueType ? FIELD_TYPE_META[valueType] : null;
  const SelectedTypeIcon = selectedTypeMeta?.Icon;

  return (
    <Box position="relative" display="flex" flexDirection="column" gap="spacing.2">
      <Text weight="semibold">Data type</Text>
      <FieldTypeButton
        type="button"
        $isOpen={isOpen}
        $hasValue={Boolean(valueType)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={onToggle}>
        <Box as="span" display="inline-flex" alignItems="center" gap="spacing.3" minWidth="0px">
          {SelectedTypeIcon ? (
            <SelectedTypeIcon size="medium" color="interactive.icon.primary.normal" />
          ) : null}
          {/* Native span: the label must inherit FieldTypeButton's app-theme color
              (#E6E9EF); Blade Text only accepts Blade color tokens, which render a
              visibly different shade here. */}
          <span>{selectedOption?.label ?? 'Select type'}</span>
        </Box>
        {isOpen ? (
          <ChevronUpIcon size="medium" color="surface.icon.gray.muted" />
        ) : (
          <ChevronDownIcon size="medium" color="surface.icon.gray.muted" />
        )}
      </FieldTypeButton>
      {isOpen ? (
        <FieldTypeMenu role="listbox" aria-label="Data type options">
          {FIELD_TYPE_OPTIONS.map((option) => {
            const OptionIcon = FIELD_TYPE_META[option.value].Icon;
            const isSelected = valueType === option.value;

            return (
              <FieldTypeOption
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                $isSelected={isSelected}
                onClick={() => onSelect(option.value)}>
                <OptionIcon size="medium" color="interactive.icon.primary.normal" />
                <Box as="span" display="flex" flexDirection="column" gap="2px">
                  <Text weight="semibold">{option.label}</Text>
                  <FieldTypeOptionHelper className="field-type-option-helper">
                    {FIELD_TYPE_META[option.value].helperText}
                  </FieldTypeOptionHelper>
                </Box>
              </FieldTypeOption>
            );
          })}
        </FieldTypeMenu>
      ) : null}
      {selectedTypeMeta ? (
        <Text color="surface.text.gray.muted">{selectedTypeMeta.selectedHelperText}</Text>
      ) : null}
    </Box>
  );
};
